import type { CaptureMetadata } from "@shared/types/annotations";

import type { DepthFrame, DepthFrameFailureCause } from "./depth3d";

/** Derive a sibling depth PNG path from the capture-metadata JSON path. */
export function deriveDepthCloudPath(metadataPath: string | undefined, depthFile: string | undefined): string | null {
  if (!metadataPath || !depthFile) return null;
  return metadataPath.replace(/[^/]+$/, () => depthFile);
}

/**
 * Whether a depth-frame load failure is worth retrying. `Record` over the cause
 * union so adding a cause without deciding its retryability is a compile error.
 */
const DEPTH_FRAME_FAILURE_RETRYABLE: Record<DepthFrameFailureCause, boolean> = {
  "fetch-failed": true,
  "object-missing": false,
  "decode-failed": false,
  "missing-descriptor": false,
  misaligned: false,
  "dimension-mismatch": false,
  "frame-invalid": false,
};

export function isRetryableDepthFrameFailure(cause: DepthFrameFailureCause): boolean {
  return DEPTH_FRAME_FAILURE_RETRYABLE[cause];
}

/**
 * Whether a capture's measurement endpoints can be edited in 3D (depth loaded),
 * in 2D (the capture provably never had depth), or not at all.
 *
 * The decision needs two independent pieces of evidence — the capture metadata
 * (does the capture DESCRIBE depth?) and the entry's stored depth path (was a
 * depth file actually uploaded?) — because either one alone is ambiguous. 2D
 * editing therefore unlocks only on positive evidence of a depth-less capture;
 * anything unknown stays locked so a real depth capture is never silently
 * downgraded to depth-free (and unrecomputed) measurements.
 */
export type DepthFailureCause =
  | DepthFrameFailureCause
  | "metadata-fetch-failed"
  /** The capture describes depth but the entry has no depth file (expected-incomplete). */
  | "entry-depth-path-missing"
  /** The entry has a depth file the capture does not describe (inconsistent). */
  | "entry-depth-path-orphaned";

export type DepthAvailability =
  | { status: "metadata-loading" }
  | { status: "metadata-error"; cause: "metadata-fetch-failed" }
  | { status: "absent" }
  | { status: "loading"; depthCloudPath: string }
  | { status: "ready"; frame: DepthFrame }
  | { status: "retryable-error"; cause: DepthFailureCause }
  | { status: "terminal-error"; cause: DepthFailureCause };

export type DepthEvidenceDecision =
  | { decision: "wait" }
  | { decision: "metadata-error" }
  | { decision: "absent" }
  | { decision: "load"; depthCloudPath: string }
  | { decision: "expected-incomplete" }
  | { decision: "inconsistent" };

/** Classify what the capture metadata + the entry's stored depth path say about
 *  this capture's depth, without fetching anything. */
export function classifyDepthEvidence(args: {
  captureMetadata: CaptureMetadata | null | undefined;
  metadataFailed: boolean;
  metadataPath: string | undefined;
  /** `undefined` = the evidence was never plumbed through; `null` = consulted and absent. */
  entryDepthCloudPath: string | null | undefined;
}): DepthEvidenceDecision {
  const { captureMetadata, metadataFailed, metadataPath, entryDepthCloudPath } = args;

  if (metadataFailed) return { decision: "metadata-error" };
  if (!captureMetadata) return { decision: "wait" };

  // The server's geometry-edit absence check is `!captureMetadata.depth`, so a
  // depth block ALONE marks a depth capture here — even without intrinsics.
  // (Depth without intrinsics can never build a frame; the load path surfaces
  // that as a terminal missing-descriptor failure instead of unlocking 2D.)
  const describesDepth = Boolean(captureMetadata.depth);
  const storedPath =
    typeof entryDepthCloudPath === "string" && entryDepthCloudPath.length > 0 ? entryDepthCloudPath : null;

  if (describesDepth) {
    if (!storedPath) return { decision: "expected-incomplete" };
    const depthCloudPath = deriveDepthCloudPath(metadataPath, captureMetadata.depth.file);
    if (!depthCloudPath) return { decision: "expected-incomplete" };
    if (depthCloudPath !== storedPath) {
      // The capture's sibling depth file is the one the metadata's intrinsics
      // describe, so it wins — but a disagreement means one of the two is stale.
      console.warn(
        `classifyDepthEvidence: entry depth path ${storedPath} disagrees with the capture's sibling depth ${depthCloudPath}; loading the sibling`
      );
    }
    return { decision: "load", depthCloudPath };
  }

  if (storedPath) return { decision: "inconsistent" };
  // Only a consulted-and-empty entry path proves the capture never had depth;
  // `undefined` means nobody looked, so stay locked instead of unlocking 2D.
  if (entryDepthCloudPath === undefined) return { decision: "expected-incomplete" };
  return { decision: "absent" };
}

export type MeasurementEditingMode = { kind: "locked" } | { kind: "2d" } | { kind: "3d"; depthFrame: DepthFrame };

/** Map resolved depth availability onto what the editor may actually do. */
export function deriveMeasurementEditingMode(
  availability: DepthAvailability | null | undefined
): MeasurementEditingMode {
  if (availability?.status === "ready") return { kind: "3d", depthFrame: availability.frame };
  if (availability?.status === "absent") return { kind: "2d" };
  return { kind: "locked" };
}
